import type { JSX } from "react";
import { lazy as reactLazy, Suspense } from "react";

import { unsafelyKeys } from "common/utilities/object";
import { Fallback } from "router/Fallback";

type LazyProps = { load: () => Promise<Record<PropertyKey, () => JSX.Element>> };

export const Lazy = ({ load }: LazyProps) => {
  const Component = lazy(load);
  return (
    <Suspense fallback={<Fallback />}>
      <Component />
    </Suspense>
  );
};

/**
 * Wraps `React.lazy` for handling files with only named exports.
 *
 * @param load - A function that returns a promise that resolves to an object containing lazy-loaded components.
 * @param name - Optional. The name of the component to load from the object. If not provided, the first component in the object will be used.
 * @returns A lazily loaded component.
 */
const lazy = <T extends PropertyKey>(load: () => Promise<Record<T, () => JSX.Element>>, name?: T) =>
  reactLazy(() =>
    load().then((obj) => {
      const key = unsafelyKeys(obj).at(0);
      return { default: name ? obj[name] : key ? obj[key] : () => null };
    }),
  );
